import { useEffect, useCallback, useRef, useState } from 'react';
import { jwtDecode } from 'jwt-decode';
import { useAuth } from './AuthProvider';
import { useConfig } from '@/app/ConfigProvider';

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: Record<string, unknown>) => void;
          renderButton: (element: HTMLElement, config: Record<string, unknown>) => void;
          prompt: () => void;
        };
      };
    };
  }
}

interface GoogleSignInButtonProps {
  onSuccess?: () => void;
}

export function GoogleSignInButton({ onSuccess }: GoogleSignInButtonProps) {
  const { login } = useAuth();
  const config = useConfig();
  const buttonRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);

  const handleCredentialResponse = useCallback(
    (response: { credential: string }) => {
      try {
        const token = response.credential;
        const payload = jwtDecode<{ sub: string; email: string; name: string; picture: string }>(token);

        login(
          {
            id: payload.sub,
            email: payload.email,
            name: payload.name,
            picture: payload.picture,
          },
          token
        );

        onSuccess?.();
      } catch {
        setError('Failed to process sign-in response. Please try again.');
      }
    },
    [login, onSuccess]
  );

  useEffect(() => {
    const clientId = config.auth.google.clientId;
    if (!clientId) {
      setError('Google sign-in is not configured. Please set the Google client ID in config.json.');
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = () => {
      window.google?.accounts.id.initialize({
        client_id: clientId,
        callback: handleCredentialResponse,
      });

      if (buttonRef.current) {
        window.google?.accounts.id.renderButton(buttonRef.current, {
          type: 'standard',
          theme: 'outline',
          size: 'large',
          width: 320,
          text: 'continue_with',
        });
      }
    };
    script.onerror = () => {
      setError('Failed to load Google sign-in. Check your network connection.');
    };

    document.body.appendChild(script);

    return () => {
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, [config.auth.google.clientId, handleCredentialResponse]);

  if (error) {
    return (
      <div className="text-center text-sm text-destructive p-4 rounded-lg border border-destructive/20 bg-destructive/5">
        {error}
      </div>
    );
  }

  return <div ref={buttonRef} className="flex justify-center min-h-[44px]" />;
}
